import { Injectable, inject } from "@angular/core";
import { Observable, catchError, forkJoin, map, of } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { DiningTableService } from "../../catalog-storefront/dining-tables/dining-table.service";
import { SalePointService } from "../../sale-points/sale-point.service";
import { DeliveryMethodApiService } from "../../../services/generated/erp-api/services/delivery-method-api.service";
import { OrderApiService } from "../../../services/generated/erp-api/services/order-api.service";
import { ProductApiService } from "../../../services/generated/erp-api/services/product-api.service";
import { SILENT_HTTP_CONTEXT } from "../../../shared/utils/silent-http.util";

/** Done state of each "Mon restaurant en 5 étapes" step (ids of RESTAURANT_SETUP_STEPS). */
export interface RestaurantSetupState {
  "restaurant-dishes": boolean;
  "restaurant-map": boolean;
  "restaurant-zones": boolean;
  "restaurant-tables": boolean;
  "restaurant-test-order": boolean;
}

/**
 * Checks the five restaurant steps against the restaurant's real data: at least one dish, a sales point placed
 * on the map, an active delivery method with a zone, at least one table, and one order already received.
 * A check that fails to load counts as not done (the step stays open, nothing breaks).
 */
@Injectable({ providedIn: "root" })
export class RestaurantSetupService {
  private readonly productApi = inject(ProductApiService);
  private readonly salePoints = inject(SalePointService);
  private readonly deliveryMethodApi = inject(DeliveryMethodApiService);
  private readonly diningTables = inject(DiningTableService);
  private readonly orderApi = inject(OrderApiService);

  private state: Partial<RestaurantSetupState> = {};

  isDone(stepId: string): boolean {
    return !!this.state[stepId as keyof RestaurantSetupState];
  }

  refresh(): void {
    this.load().subscribe((state) => (this.state = state));
  }

  load(): Observable<RestaurantSetupState> {
    const headers = () => ({ "x-api-canal": "web", "x-api-requestId": uuidv4() });
    const safe = (check: Observable<boolean>) => check.pipe(catchError(() => of(false)));
    return forkJoin({
      "restaurant-dishes": safe(
        this.productApi
          .searchProduct(
            { ...headers(), body: { page: 0, size: 1, filter: { archived: false, itemType: "PRODUCT" } } },
            SILENT_HTTP_CONTEXT,
          )
          .pipe(map((response) => (response.body?.entities?.length ?? 0) > 0)),
      ),
      "restaurant-map": safe(
        this.salePoints
          .list()
          .pipe(map((points) => points.some((point) => point.active !== false && isPlaced(point.pickupLat, point.pickupLng)))),
      ),
      "restaurant-zones": safe(
        this.deliveryMethodApi
          .getAvailableDeliveryMethods(headers(), SILENT_HTTP_CONTEXT)
          .pipe(map((response) => (response.body ?? []).some((method) => method.active !== false && !!method.zones?.length))),
      ),
      "restaurant-tables": safe(this.diningTables.list().pipe(map((tables) => tables.length > 0))),
      "restaurant-test-order": safe(
        this.orderApi
          .searchOrders({ ...headers(), body: { page: 0, size: 1, filter: {} } }, SILENT_HTTP_CONTEXT)
          .pipe(map((response) => (response.body?.totalElements ?? response.body?.entities?.length ?? 0) > 0)),
      ),
    });
  }
}

function isPlaced(lat: number | undefined, lng: number | undefined): boolean {
  return typeof lat === "number" && typeof lng === "number" && !(lat === 0 && lng === 0);
}
